// ==========================================
// ENUMS
// ==========================================

import { District, Mandal } from "./territorial.types";

export enum GrantLevel {
  CENTRAL = 'Central',
  STATE = 'State'
}

export enum GrantType {
  PMGSY = 'PMGSY',
  CRR = 'CRR',
  MRR = 'MRR',
  FDR = 'FDR'
}

export enum WorkStage {
  NOT_STARTED = 'Not Started',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  ON_HOLD = 'On Hold',
  PLANNING='planning',
}

export enum CategoryPST {
  PLAIN = 'P',
  SCP = 'S',
  TSP = 'T'
}

export enum WorkType {
  NEW_BT_ROAD = 'New BT Road',
  UPGRADATION = 'Upgradation',
  REHABILITATION = 'Rehabilitation',
  MAINTENANCE = 'Maintenance'
}



export enum WorkCategory {
  RURAL_ROADS = 'Rural Roads',
  URBAN_ROADS = 'Urban Roads',
  BRIDGES = 'Bridges',
  CULVERTS = 'Culverts'
}

// ==========================================
// CORE INTERFACES
// ==========================================

export interface Contractor {
  contractor_id: number;
  contractor_name: string;
  firm_name: string;
  pan_number: string;
  contact_number?: string;
  bank_name?: string;
  is_active: boolean;
}

export interface Work {
  district_name: number;
  category: string;
  work_id: number;
  sno: number;
  oas_grant_level: GrantLevel;
  grant_type: string;
  district_id: number;
  mandal_id: number;
  work_name: string;
  oas_proceedings_number: string;
  oas_sanction_date: string;
  work_type: string;
  work_sub_type?: string;
  length_sanctioned_km?: number;
  estimate_cost: number;
  agency_name: string;
  tp?: string;
  agreement_date?: string;
  completion_date_set?: string;
  ras_amount?: number;
  ras_proceedings_number?: string;
  ras_sanction_date?: string;
  abstract_stage_of_work: WorkStage;
  sub_stage_of_work?: string;
  length_completed_upto_31_03_2023: number;
  balance_length_spilled_over_fy_23_24: number;
  length_completed_during_fy_23_24: number;
  cumulative_length_completed: number;
  total_exp_upto_31_03_2023: number;
  balance_est_cost_spilled_over_fy_23_24: number;
  exp_during_fy_23_24: number;
  cum_exp_on_work: number;
  category_pst?: CategoryPST;
  detailed_stage_of_work?: string;
  contractor_id?: number;
  created_by?: number;
  updated_by?: number;
  is_active: boolean;
  createdAt?: string;
  updatedAt?: string;
  
  // Relationships
  district?: District;
  mandal?: Mandal;
  contractor?: Contractor;
  bills_pending_pao_bro_loc?: number; // Add this
  work_done_not_recorded_bills?: number; // Add this
}

// ==========================================
// DTO INTERFACES
// ==========================================

export interface CreateWorkDto {
  sno: number;
  oas_grant_level: GrantLevel;
  grant_type: string;
  district_id: number;
  mandal_id: number;
  work_name: string;
  oas_proceedings_number: string;
  oas_sanction_date: string;
  work_type: string;
  work_sub_type?: string;
  length_sanctioned_km?: number;
  estimate_cost: number;
  agency_name: string;
  tp?: string;
  agreement_date?: string;
  completion_date_set?: string;
  ras_amount?: number;
  ras_proceedings_number?: string;
  ras_sanction_date?: string;
  abstract_stage_of_work: WorkStage;
  sub_stage_of_work?: string;
  length_completed_upto_31_03_2023?: number;
  balance_length_spilled_over_fy_23_24?: number;
  length_completed_during_fy_23_24?: number;
  total_exp_upto_31_03_2023?: number;
  balance_est_cost_spilled_over_fy_23_24?: number;
  exp_during_fy_23_24?: number;
  bills_pending_pao_bro_loc?: number;
  work_done_not_recorded_bills?: number;
  category_pst?: CategoryPST;
  detailed_stage_of_work?: string;
  contractor_id?: number;
}

export interface UpdateWorkDto extends Partial<CreateWorkDto> {}

export interface ProgressUpdateData {
  length_completed_upto_31_03_2023?: number;
  length_completed_during_fy_23_24?: number;
  balance_length_spilled_over_fy_23_24?: number;
  total_exp_upto_31_03_2023?: number;
  exp_during_fy_23_24?: number;
  balance_est_cost_spilled_over_fy_23_24?: number;
  abstract_stage_of_work?: WorkStage;
  sub_stage_of_work?: string;
  detailed_stage_of_work?: string;
  bills_pending_pao_bro_loc?: number;
  work_done_not_recorded_bills?: number;
  category_pst?: CategoryPST;
}

// ==========================================
// FILTER & RESPONSE INTERFACES
// ==========================================

export interface WorkFilters {
  page?: number;
  limit?: number;
  districtId?: number;
  mandalId?: number;
  grantLevel?: GrantLevel;
  grantType?: string;
  workType?: string;
  stage?: WorkStage;
  subStage?: string;
  category?: CategoryPST;
  fromDate?: Date;
  toDate?: Date;
  minCost?: number;
  maxCost?: number;
  searchTerm?: string;
  contractorId?: number;
  agencyName?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  [key: string]: any; // Allow dynamic key access
}

export interface WorkSummary {
  total_works: number;
  total_estimate_cost: number;
  total_ras_amount: number;
  total_expenditure: number;
  total_length_sanctioned: number;
  total_length_completed: number;
  total_bills_pending: number;
  total_work_not_recorded: number;
  financial_progress_percentage: number;
  physical_progress_percentage: number;
  avg_estimate_cost: number;
  avg_completion_percentage: number;
}

export interface WorkListResponse {
  works: Work[];
  total: number;
  totalPages: number;
  currentPage: number;
  summary: WorkSummary;
  filters: WorkFilters;
}

export interface BulkOperationResult {
  successful: number;
  failed: number;
  errors: Array<{
    index?: number;
    work_id?: number;
    sno?: number;
    error: string;
  }>;
  createdWorks?: Work[];
}

export interface DeleteWorkResponse {
  message: string;
  work: Work;
}

// ==========================================
// DASHBOARD & ANALYTICS INTERFACES
// ==========================================

export interface DashboardOverview {
  total_works: number;
  total_sanctioned: number;
  total_ras: number;
  total_spent: number;
  pending_with_pao: number;
  work_done_not_billed: number;
  avg_work_cost: number;
  utilization_percentage: number;
  pending_bills_percentage: number;
}

export interface TrendAnalysis {
  month: string;
  works_sanctioned: number;
  amount_sanctioned: number;
  amount_spent: number;
}

export interface DistrictSummary {
  district_id: number;
  district_name: string;
  total_works: number;
  total_estimate_cost: number;
  total_ras_amount: number;
  total_expenditure: number;
  total_length_sanctioned: number;
  total_length_completed: number;
  total_bills_pending: number;
  total_work_not_recorded: number;
  avg_estimate_cost: number;
  financial_progress_percentage: number;
  physical_progress_percentage: number;
  avg_completion_percentage:number;
}

export interface StageSummary {
  abstract_stage_of_work: WorkStage;
  work_count: number;
  total_estimate_cost: number;
  total_expenditure: number;
  avg_estimate_cost: number;
  total_length_sanctioned: number;
  total_length_completed: number;
}

export interface GrantTypeAnalysis {
  oas_grant_level: string;
  grant_type: string;
  work_count: number;
  total_sanctioned: number;
  total_spent: number;
  avg_cost: number;
}

export interface DashboardData {
  overview: DashboardOverview;
  trendAnalysis: TrendAnalysis[];
  districtComparison: DistrictSummary[];
  grantTypeAnalysis: GrantTypeAnalysis[];
  expenditureVsAllocation: DashboardOverview;
}

export interface ProgressTracking {
  physicalProgress: Array<{
    district_id: number;
    district_name: string;
    total_sanctioned_length: number;
    total_completed_length: number;
    total_works: number;
    completion_percentage: number;
  }>;
  financialProgress: Array<{
    abstract_stage_of_work: WorkStage;
    work_count: number;
    total_sanctioned: number;
    total_spent: number;
    financial_progress: number;
  }>;
  stageWiseProgress: StageSummary[];
  delayedWorks: Array<{
    work_id: number;
    sno: number;
    work_name: string;
    agency_name: string;
    completion_date_set: string;
    abstract_stage_of_work: WorkStage;
    estimate_cost: number;
    cum_exp_on_work: number;
    district_name: string;
    mandal_name: string;
  }>;
  completionForecast: {
    stage_wise_forecast: Array<{
      stage: WorkStage;
      estimated_completion_months: number;
      confidence_level: number;
    }>;
    total_works_in_progress: number;
    total_remaining_cost: number;
  };
}

export interface PerformanceAnalytics {
  contractorPerformance: Array<{
    contractor_id: number;
    contractor_name: string;
    total_works: number;
    total_contract_value: number;
    total_expenditure: number;
    avg_project_duration: number;
    completed_works: number;
    delayed_works: number;
    performance_score: number;
  }>;
  workTypeAnalysis: Array<{
    work_type: string;
    work_sub_type: string;
    work_count: number;
    total_cost: number;
    avg_cost: number;
    total_length: number;
    avg_planned_duration: number;
    completed_count: number;
    completion_rate: number;
  }>;
  timeAnalysis: Array<{
    year: number;
    month: number;
    works_sanctioned: number;
    total_sanctioned_amount: number;
    avg_work_cost: number;
    completed_works: number;
    avg_completion_time_days: number;
  }>;
  costVarianceAnalysis: Array<{
    work_id: number;
    sno: number;
    work_name: string;
    estimate_cost: number;
    ras_amount: number;
    cum_exp_on_work: number;
    cost_escalation: number;
    escalation_percentage: number;
    expenditure_variance: number;
    variance_percentage: number;
    district_name: string;
    contractor_name?: string;
  }>;
}

// ==========================================
// EXPORT & IMPORT INTERFACES
// ==========================================

export interface ExcelExportResult {
  data: Array<Record<string, any>>;
  summary: WorkSummary;
  filename: string;
}

export interface ImportValidationResult {
  valid: Array<{ row: number; data: any }>;
  invalid: Array<{ row: number; data: any; errors: string[] }>;
  summary: {
    total: number;
    valid: number;
    invalid: number;
    duplicates: number;
  };
}

// ==========================================
// FORM & UI INTERFACES
// ==========================================

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'textarea';
  required?: boolean;
  options?: Array<{ value: string | number; label: string }>;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  validation?: {
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    custom?: (value: any) => string | null;
  };
}

export interface PageInfo {
  title: string;
  description: string;
  icon: React.ComponentType;
  path: string;
}

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, record: Work) => React.ReactNode;
}

export interface QuickStats {
  totalWorks: number;
  totalSanctioned: number;
  totalSpent: number;
  utilizationPercentage: number;
  completedWorks: number;
  delayedWorks: number;
  pendingBillsAmount: number;
}

export interface FieldOptions {
  grantLevels: GrantLevel[];
  grantTypes: string[];
  workCategories: WorkCategory[];
  workTypes: WorkType[];
  workStages: WorkStage[];
  categories: CategoryPST[];
  subStages: string[];
}

export interface ValidationRules {
  [key: string]: {
    required?: boolean;
    type: string;
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    maxDecimalPlaces?: number;
    unique?: boolean;
    enum?: string[];
    businessRule?: string;
    description: string;
  };
}

// ==========================================
// API RESPONSE INTERFACES
// ==========================================

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  timestamp: string;
}

export interface PaginatedResponse<T> {
  works: Work[] | PromiseLike<Work[]>;
  summary: WorkSummary;
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  database: 'connected' | 'disconnected';
  version: string;
  uptime: number;
  error?: string;
}

// ==========================================
// UTILITY TYPES
// ==========================================

export type SortOrder = 'ASC' | 'DESC';

export type WorkStatus = 'all' | 'active' | 'completed' | 'delayed' | 'pending_bills';

export interface SearchCriteria {
  workName?: string;
  proceedings?: string;
  agency?: string;
  contractor?: string;
  stages?: string[];
  districts?: number[];
  mandals?: number[];
  costRange?: string;
  dateRange?: string;
  lengthRange?: string;
  completionStatus?: string;
  delayedOnly?: boolean;
  pendingBillsOnly?: boolean;
}

export interface BatchOperation {
  workIds: number[];
  operation: 'stage_update' | 'contractor_assign' | 'delete' | 'restore';
  data?: {
    newStage?: WorkStage;
    subStage?: string;
    contractorId?: number;
  };
}

// ==========================================
// CHART & VISUALIZATION INTERFACES
// ==========================================

export interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
  percentage?: number;
}

export interface TimeSeriesData {
  date: string;
  sanctioned: number;
  spent: number;
  works_count: number;
}

export interface ProgressChartData {
  district: string;
  physical_progress: number;
  financial_progress: number;
  total_works: number;
}

export interface ComparisonData {
  category: string;
  current: number;
  target: number;
  variance: number;
}

// ==========================================
// ERROR HANDLING INTERFACES
// ==========================================

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface ApiError {
  message: string;
  code?: string;
  field?: string;
  details?: any;
}

export interface BulkOperationError {
  index?: number;
  work_id?: number;
  sno?: number;
  error: string;
}

// ==========================================
// NOTIFICATION INTERFACES
// ==========================================

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// ==========================================
// FORM STATE INTERFACES
// ==========================================

export interface WorkFormState {
  data: Partial<Work>;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  loading: boolean;
  mode: 'create' | 'edit' | 'view';
}

export interface FilterState {
  active: WorkFilters;
  saved: Array<{ name: string; filters: WorkFilters }>;
  quick: Array<{ label: string; filters: WorkFilters }>;
}

// ==========================================
// APP STATE INTERFACES
// ==========================================

export interface AppState {
  currentPage: string;
  user: {
    id: number;
    name: string;
    role: string;
  } | null;
  notifications: Notification[];
  theme: 'light' | 'dark';
  sidebarCollapsed: boolean;
}

export interface LoadingState {
  works: boolean;
  dashboard: boolean;
  analytics: boolean;
  export: boolean;
  import: boolean;
  [key: string]: boolean;
}

// ==========================================
// CONTEXT INTERFACES
// ==========================================

export interface WorksContextValue {
  works: Work[];
  summary: WorkSummary | null;
  filters: WorkFilters;
  loading: LoadingState;
  error: string | null;
  
  // Actions
  fetchWorks: (filters?: WorkFilters) => Promise<void>;
  createWork: (work: CreateWorkDto) => Promise<Work>;
  updateWork: (id: number, work: UpdateWorkDto) => Promise<Work>;
  deleteWork: (id: number) => Promise<void>;
  setFilters: (filters: WorkFilters) => void;
  clearError: () => void;
}

export interface UIContextValue {
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  notifications: Notification[];
  
  // Actions
  toggleTheme: () => void;
  toggleSidebar: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  markNotificationRead: (id: string) => void;
}

// ==========================================
// COMPONENT PROP INTERFACES
// ==========================================

export interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple';
  loading?: boolean;
}

export interface DataTableProps<T = any> {
  data: T[];
  columns: TableColumn[];
  loading?: boolean;
  pagination?: {
    current: number;
    total: number;
    pageSize: number;
    onChange: (page: number) => void;
  };
  selection?: {
    selectedRows: number[];
    onSelectionChange: (selectedRows: number[]) => void;
  };
  actions?: {
    onView?: (record: T) => void;
    onEdit?: (record: T) => void;
    onDelete?: (record: T) => void;
  };
}

export interface FilterPanelProps {
  filters: WorkFilters;
  onFiltersChange: (filters: WorkFilters) => void;
  onReset: () => void;
  loading?: boolean;
  fieldOptions?: FieldOptions;
}

export interface ChartProps {
  data: any[];
  title?: string;
  height?: number;
  loading?: boolean;
  type?: 'bar' | 'line' | 'pie' | 'area';
}

// ==========================================
// MODAL & FORM INTERFACES
// ==========================================

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  children: React.ReactNode;
}

export interface FormProps {
  work?: Work;
  mode: 'create' | 'edit' | 'view';
  onSubmit: (work: CreateWorkDto | UpdateWorkDto) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  fieldOptions?: FieldOptions;
  validationRules?: ValidationRules;
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}

// ==========================================
// NAVIGATION INTERFACES
// ==========================================

export interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: number;
  children?: NavItem[];
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
  current?: boolean;
}

// ==========================================
// UTILITY FUNCTION TYPES
// ==========================================

export type FormatFunction = (value: any) => string;
export type ValidationFunction = (value: any) => string | null;
export type TransformFunction<T, R> = (input: T) => R;

// ==========================================
// EVENT HANDLER TYPES
// ==========================================

export interface WorkEventHandlers {
  onCreate: (work: CreateWorkDto) => Promise<void>;
  onUpdate: (id: number, work: UpdateWorkDto) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onView: (work: Work) => void;
  onEdit: (work: Work) => void;
  onProgress: (id: number, progress: ProgressUpdateData) => Promise<void>;
  onExport: (filters?: WorkFilters) => Promise<void>;
  onImport: (file: File) => Promise<void>;
}

export interface FilterEventHandlers {
  onApply: (filters: WorkFilters) => void;
  onReset: () => void;
  onSave: (name: string, filters: WorkFilters) => void;
  onLoad: (filters: WorkFilters) => void;
}

// ==========================================
// CONSTANTS
// ==========================================

export const WORK_STAGES_CONFIG = {
  [WorkStage.NOT_STARTED]: {
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    progress: 0
  },
  [WorkStage.IN_PROGRESS]: {
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    progress: 50
  },
  [WorkStage.COMPLETED]: {
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    progress: 100
  },
  [WorkStage.ON_HOLD]: {
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    progress: 0
  }
};

export const GRANT_LEVEL_CONFIG = {
  [GrantLevel.CENTRAL]: {
    color: 'blue',
    icon: '🏛️'
  },
  [GrantLevel.STATE]: {
    color: 'green',
    icon: '🏢'
  }
};

export const CATEGORY_PST_CONFIG = {
  [CategoryPST.PLAIN]: {
    label: 'Plain',
    color: 'blue'
  },
  [CategoryPST.SCP]: {
    label: 'Scheduled Caste Plan',
    color: 'orange'
  },
  [CategoryPST.TSP]: {
    label: 'Tribal Sub Plan',
    color: 'green'
  }
};

export const DEFAULT_FILTERS: WorkFilters = {
  page: 1,
  limit: 50,
  sortBy: 'sno',
  sortOrder: 'ASC'
};

export const PAGE_SIZES = [10, 25, 50, 100, 250, 500];

export const DATE_FORMATS = {
  display: 'DD/MM/YYYY',
  api: 'YYYY-MM-DD',
  timestamp: 'YYYY-MM-DD HH:mm:ss'
};

export const CURRENCY_FORMAT = {
  locale: 'en-IN',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
};


// In work.types.ts
export interface MandalWorkResponse {
  summary: WorkSummary;
  works: Work[];
  total: number;
  totalPages: number;
  currentPage: number;
}


